import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Token storage (localStorage fallback for environments where cross-site /
// 3rd-party cookies are blocked, e.g. the preview being iframed by Safari /
// Chrome 3rd-party-cookie phase-out). Cookies still work on direct visits.
// ---------------------------------------------------------------------------

const ACCESS_KEY = "tt_access_token";
const REFRESH_KEY = "tt_refresh_token";
const PERSIST_FLAG_KEY = "tt_persist_session"; // "1" = persistent (localStorage), "0" = session-only

// Whether to persist auth across browser sessions. Persistent = localStorage
// (survives tab close + browser restart). Session-only = sessionStorage
// (auto-cleared when the tab closes). We remember the mode itself in
// localStorage so the same choice sticks across tabs but doesn't survive
// clearing "Site data" — a reasonable balance for a "Remember me" toggle.
function isPersistent() {
  try {
    if (typeof window === "undefined") return true;
    // Default = persistent (matches existing behavior before Remember Me
    // shipped, so no existing user is logged out by this change).
    return window.localStorage.getItem(PERSIST_FLAG_KEY) !== "0";
  } catch {
    return true;
  }
}

function safeStorage() {
  try {
    if (typeof window === "undefined") return null;
    return isPersistent() ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

// Toggle "Remember me" for future logins. Called by the Login form BEFORE
// setAccessToken(...) so the token lands in the right storage. When the
// user unchecks Remember me on an already-signed-in device, we migrate any
// existing tokens from localStorage → sessionStorage (or vice versa) so
// the checkbox has an immediate effect.
export function setPersistSession(remember) {
  try {
    if (typeof window === "undefined") return;
    const nextFlag = remember ? "1" : "0";
    // Read current tokens (using the previous mode) before we flip the flag.
    const prev = safeStorage();
    const access = prev?.getItem(ACCESS_KEY) || null;
    const refresh = prev?.getItem(REFRESH_KEY) || null;
    window.localStorage.setItem(PERSIST_FLAG_KEY, nextFlag);
    // Clear the old store so a stale copy doesn't shadow the new one.
    try { window.localStorage.removeItem(ACCESS_KEY); window.localStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
    try { window.sessionStorage.removeItem(ACCESS_KEY); window.sessionStorage.removeItem(REFRESH_KEY); } catch { /* ignore */ }
    // Re-plant existing tokens (if any) into the new store so the user
    // doesn't get logged out just by toggling the checkbox.
    const next = safeStorage();
    if (access) next?.setItem(ACCESS_KEY, access);
    if (refresh) next?.setItem(REFRESH_KEY, refresh);
  } catch { /* ignore */ }
}

export function getPersistSession() { return isPersistent(); }

export function getAccessToken() {
  return safeStorage()?.getItem(ACCESS_KEY) || null;
}
export function setAccessToken(t) {
  const s = safeStorage();
  if (!s) return;
  if (t) s.setItem(ACCESS_KEY, t);
  else s.removeItem(ACCESS_KEY);
}
export function getRefreshToken() {
  return safeStorage()?.getItem(REFRESH_KEY) || null;
}
export function setRefreshToken(t) {
  const s = safeStorage();
  if (!s) return;
  if (t) s.setItem(REFRESH_KEY, t);
  else s.removeItem(REFRESH_KEY);
}
export function clearTokens() {
  // Wipe both storages so a stale token from a previous "Remember me" state
  // can't accidentally re-authenticate the browser after logout.
  setAccessToken(null);
  setRefreshToken(null);
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(ACCESS_KEY);
      window.localStorage.removeItem(REFRESH_KEY);
      window.sessionStorage.removeItem(ACCESS_KEY);
      window.sessionStorage.removeItem(REFRESH_KEY);
    }
  } catch { /* ignore */ }
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach Authorization header from localStorage on every request (no-op in
// browsers where cookies just work — backend accepts either source).
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Global 401 handling
// ---------------------------------------------------------------------------

let onUnauthorized = null;
let unauthorizedTriggered = false;

export function registerUnauthorizedHandler(fn) {
  onUnauthorized = fn;
  unauthorizedTriggered = false;
}

export function resetUnauthorizedFlag() {
  unauthorizedTriggered = false;
}

// Endpoints that are *expected* to 4xx for unauthenticated callers and whose
// errors must propagate to the calling component for inline UX (login form,
// invite-accept form, etc.) instead of triggering a session-expired redirect.
//
// `/orgs/mine` and `/orgs/mine/plan` are listed here because BrandProvider
// probes them on first mount to detect the current org's industry profile —
// for guest visitors that probe legitimately 401s and MUST NOT trigger a
// hard-redirect to /login (which was the P0 blank-white bug caused by the
// Industry Profiles refactor).
const SILENT_AUTH_PATHS = [
  "/auth/me",
  "/auth/refresh",
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/invites/accept",
  "/orgs/mine",
  "/orgs/mine/plan",
];

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = (err?.config?.url || "").replace(API_BASE, "");

    if (status === 401) {
      if (SILENT_AUTH_PATHS.some((p) => url.includes(p))) {
        // Stale tokens — wipe so we don't keep sending them
        clearTokens();
        return Promise.reject(err);
      }
      if (!unauthorizedTriggered) {
        unauthorizedTriggered = true;
        clearTokens();
        try { onUnauthorized && onUnauthorized(); } catch (_) { /* noop */ }
      }
      return new Promise(() => { /* swallow forever — page is unmounting */ });
    }

    return Promise.reject(err);
  }
);

export default api;

// ---------------------------------------------------------------------------
// Helpers (unchanged)
// ---------------------------------------------------------------------------

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

export function formatCurrency(n) {
  const v = typeof n === "number" ? n : Number(n || 0);
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    // Date-only strings ("2026-10-01") must be treated as calendar dates,
    // not UTC instants. new Date("2026-10-01") parses as UTC midnight and
    // then toLocaleDateString shifts it back a day in any UTC-negative
    // timezone (all of the Americas). Build a local Date from the
    // YYYY-MM-DD components instead so the rendered date always equals
    // the calendar date the coach picked, regardless of the viewer's TZ.
    let d;
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, day] = iso.split("-").map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(iso);
    }
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export function daysUntil(iso) {
  if (!iso) return null;
  // Same date-only handling as formatDate — parse as local calendar date
  // so the DueChip's overdue/due-soon window agrees with what the coach
  // sees rendered on the fee card. Full ISO timestamps still parse
  // normally.
  let d;
  if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, day] = iso.split("-").map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(iso);
  }
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const delta = Math.round((d - today) / (1000 * 60 * 60 * 24));
  return delta;
}

/** Canonical payment method labels — single source of truth for display.
 *  Backend stores lowercase tokens (venmo / zelle / cashapp / cash / check / other);
 *  this helper turns them into proper Title-Cased product strings.
 */
const METHOD_LABELS = {
  venmo: "Venmo",
  zelle: "Zelle",
  cashapp: "Cash App",
  cash: "Cash",
  check: "Check",
  other: "Other",
};

export function formatMethod(method) {
  if (!method) return "";
  const key = String(method).toLowerCase().trim();
  return METHOD_LABELS[key] || method;
}

